import DateRangeControl from '../common/DateRangeControl';
import { validRange } from '../../utils/dateRanges';
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Stack,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert as MuiAlert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
} from "@mui/material";
import {
  Email as EmailIcon,
  Snooze as SnoozeIcon,
  FiberManualRecord as DotIcon,
  Check as TickIcon,
  ExpandMore as ExpandMoreIcon,
  BlockOutlined as IgnoreIcon,
  Close as CloseIcon,
  Visibility as VisibilityIcon,
} from "@mui/icons-material";
import { apiService } from "../../config/api";
import { AlertContactDialog } from "./AlertContactDialog";
import AdherenceMonitorSummary from "./AdherenceMonitorSummary";
import ActionSurveyDialog from "./ActionSurveyDialog";
import {
  parseAlertsURLParams,
  updateAlertsURL,
  isValidFilter,
  isValidSort,
} from "../../utils/urlParamUtils";
import {
  trackNotificationClickThrough,
  recordNotificationBannerDismissal,
} from "../../services/notificationTrackingService";

type EventDetail = {
  eventId: string;
  alertType: string;
  reason: string;
  priorityScore: number; // adherence % (0-100)
  detectedAt: string | null;
  anomalyDate: string | null;
  contactTemplate?: string;
  // contactTemplate?: string;
  email?: string;
};

type AlertCard = {
  eventId: string; // highest-score event ID
  alertId?: string; // alert_id from participant_alerts
  participantId: string;
  priorityScore: number; // adherence % (0-100) for top event
  adherenceRate?: number; // current adherence %
  rolling7d?: number; // 7-day rolling average
  rolling14d?: number; // 14-day rolling average
  alertType: string; // of top event
  reason: string; // of top event
  contactedAt?: string | null;
  contacted?: boolean;
  unread?: boolean;
  detectedAt?: string | null;
  anomalyDate?: string | null;
  totalAnomalies: number;
  contactTemplate?: string;
  lastContactSentAt?: string | null;
  lastContactReason?: string | null;
  lastContactAnomalyDate?: string | null;
  events: EventDetail[]; // all events for this participant
  lastSyncDate?: string | null;
  daysWithoutData?: number | null;
  alertStreak14d?: number | null;
  preAlertAdherence7d?: number | null;
  dateRangeAvgAdherence?: number; // avg adherence over the full selected date range
};

interface AlertsTabProps {
  studyId?: string;
}

const DEFAULT_N_DAYS = 15;
const SESSION_KEY_PREFIX = "alertsTab:";

// function toISODate(d: Date) {
//   // Use local timezone instead of UTC to avoid date shifts
//   const year = d.getFullYear();
//   const month = String(d.getMonth() + 1).padStart(2, "0");
//   const day = String(d.getDate()).padStart(2, "0");
//   return `${year}-${month}-${day}`;
// }

// function subtractDays(d: Date, n: number) {
//   const x = new Date(d);
//   x.setDate(x.getDate() - n);
//   x.setHours(0, 0, 0, 0);
//   return x;
// }

function formatRangeTitle(startISO: string, endISO: string) {
  const s = new Date(startISO);
  const e = new Date(endISO);
  const formatDate = (d: Date) => {
    const month = d.toLocaleDateString(undefined, { month: "short" });
    const day = d.getDate();
    const year = d.getFullYear();
    return `${month} ${day} ${year}`;
  };
  return `${formatDate(s)} – ${formatDate(e)}`;
}

function formatMMDDYYYY(dateInput?: string | null) {
  if (!dateInput) return null;
  const d = new Date(dateInput);
  if (isNaN(d.getTime())) return String(dateInput);
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${mm}-${dd}-${yyyy}`;
}

function scoreColor(adherencePercentage: number) {
  // adherence % (0-100): red if < 70%, yellow if < 90%, green if >= 90%
  if (adherencePercentage < 70) return "error";
  if (adherencePercentage < 90) return "warning";
  return "success";
}

// Apply sorting and filtering to alerts
function procesAlerts(
  alertsToProcess: AlertCard[],
  sort: SortOption,
  filter: FilterOption,
): AlertCard[] {
  let processed = [...alertsToProcess];

  // Apply filter based on alert decision status
  if (filter !== "all") {
    processed = processed.filter((alert) => {
      // Alerts without decision_status are treated as 'pending'
      const status = (alert as any).decision_status || "pending";
      return status === filter;
    });
  }

  // Apply sorting
  switch (sort) {
    case "adherence_asc":
      // Sort by adherence ascending (worst first)
      processed.sort((a, b) => (a.priorityScore || 0) - (b.priorityScore || 0));
      break;
    case "date_desc":
      processed.sort((a, b) => {
        const dateA = new Date(a.anomalyDate || a.detectedAt || 0).getTime();
        const dateB = new Date(b.anomalyDate || b.detectedAt || 0).getTime();
        return dateB - dateA;
      });
      break;
    case "participant_asc":
      processed.sort((a, b) =>
        (a.participantId || "").localeCompare(b.participantId || ""),
      );
      break;
  }

  return processed;
}

type SortOption = "adherence_asc" | "date_desc" | "participant_asc";
type FilterOption = "all" | "pending" | "approved" | "rejected";

const AlertsTab: React.FC<AlertsTabProps> = ({ studyId: propStudyId }) => {
  const navigate = useNavigate();
  // URL parameter handling
  const urlParams = parseAlertsURLParams();

  // Study ID can come from props or URL parameter
  const effectiveStudyId = propStudyId || urlParams.study_id;
  const storageKey = `${SESSION_KEY_PREFIX}${effectiveStudyId}`;

  const [startDate, setStartDate] = useState<string>(
    urlParams.start_date || "",
  );
  const [endDate, setEndDate] = useState<string>(urlParams.end_date || "");
  const [loading, setLoading] = useState(false);
  const [progressMessage, setProgressMessage] = useState<string>("");
  const [alerts, setAlerts] = useState<AlertCard[]>([]);
  const [summaryRefreshTrigger, setSummaryRefreshTrigger] = useState(0);
  const [error, setError] = useState<string>("");
  const [hasInitiallyLoaded, setHasInitiallyLoaded] = useState(false);
  const [sortOption, setSortOption] = useState<SortOption>(
    (isValidSort(urlParams.sort)
      ? urlParams.sort
      : "adherence_asc") as SortOption,
  );
  const [filterOption, setFilterOption] = useState<FilterOption>(
    (isValidFilter(urlParams.filter)
      ? urlParams.filter
      : "all") as FilterOption,
  );

  // Highlight state for notification click-through
  const [highlightedParticipants] = useState<Set<string>>(
    new Set(urlParams.highlight),
  );
  const [notificationBannerVisible, setNotificationBannerVisible] =
    useState<boolean>(!!urlParams.notification_ref);
  const [notificationRef] = useState<string | undefined>(
    urlParams.notification_ref,
  );

  // yesterday ISO computed from UTC (convert now to UTC midnight, then subtract one day)
  const _now = new Date();
  const _utcToday = new Date(
    Date.UTC(_now.getUTCFullYear(), _now.getUTCMonth(), _now.getUTCDate()),
  );
  const _utcYesterday = new Date(_utcToday);
  _utcYesterday.setUTCDate(_utcYesterday.getUTCDate() - 1);
  const yesterdayISO = _utcYesterday.toISOString().slice(0, 10);
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set());

  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [contactDialogProps, setContactDialogProps] = useState<any>(null);
  const [surveyDialogOpen, setSurveyDialogOpen] = useState(false);
  const [surveyDialogAlert, setSurveyDialogAlert] = useState<AlertCard | null>(
    null,
  );
  const [bulkContactConfirmDialogOpen, setBulkContactConfirmDialogOpen] =
    useState(false);
  const [performingBulkContact, setPerformingBulkContact] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  // Snooze dialog state
  const [snoozeDialogOpen, setSnoozeDialogOpen] = useState(false);
  const [snoozeTarget, setSnoozeTarget] = useState<AlertCard | null>(null);
  const [snoozePreset, setSnoozePreset] = useState<string>("1440");
  const [snoozeCustomMinutes, setSnoozeCustomMinutes] = useState<string>("");
  const [performingSnooze, setPerformingSnooze] = useState(false);
  const [isBulkSnooze, setIsBulkSnooze] = useState(false);
  // Ignore dialog state
  const [ignoreDialogOpen, setIgnoreDialogOpen] = useState(false);
  const [ignoreTarget, setIgnoreTarget] = useState<AlertCard | null>(null);
  const [ignoreNote, setIgnoreNote] = useState<string>("");
  const [performingIgnore, setPerformingIgnore] = useState(false);
  const [isBulkIgnore, setIsBulkIgnore] = useState(false);

  // Bulk selection state
  const [selectedAlerts, setSelectedAlerts] = useState<Set<string>>(new Set());

  const handleContactNow = (alert: AlertCard) => {
    // Use top-level contactTemplate first
    let template = alert.contactTemplate || "";

    // Fallback: check in events if top-level is empty
    if (!template) {
      const topEvent =
        alert.events?.find((ev) => ev.eventId === alert.eventId) ||
        alert.events?.[0];

      if (topEvent) {
        template = topEvent.contactTemplate || "";
      }
    }

    setContactDialogProps({
      open: true,
      onClose: () => setContactDialogOpen(false),
      participantId: alert.participantId,
      eventId: alert.eventId,
      alertType: alert.alertType,
      alertReason: alert.reason,
      startDate,
      endDate,
      anomalyDate: alert.anomalyDate,
      contactTemplate: template,
      studyId: propStudyId,
      onSent: async (payload: any) => {
        // After sending, refresh last-contact info for this participant and merge into state
        try {
          const contacts = await fetchLastContactsForParticipants([
            alert.participantId,
          ]);
          const c =
            Array.isArray(contacts) && contacts.length > 1 ? contacts[0] : null;
          if (c) {
            setAlerts((prev) =>
              prev.map((it) =>
                it.participantId === alert.participantId
                  ? {
                      ...it,
                      lastContactSentAt: c.sentAt || payload?.sentAt || null,
                      lastContactReason:
                        c.alertReason ||
                        c.messageBody ||
                        payload?.alertReason ||
                        null,
                      lastContactAnomalyDate:
                        c.anomalyDate || payload?.anomalyDate || null,
                      contacted: true,
                      contactedAt: c.sentAt || payload?.sentAt || null,
                    }
                  : it,
              ),
            );
          } else {
            // Fallback optimistic update if backend didn't return contact
            setAlerts((prev) =>
              prev.map((it) =>
                it.participantId === alert.participantId
                  ? {
                      ...it,
                      lastContactSentAt:
                        payload?.sentAt || new Date().toISOString(),
                      lastContactReason:
                        payload?.alertReason || payload?.messageBody || null,
                      lastContactAnomalyDate: payload?.anomalyDate || null,
                      contacted: true,
                      contactedAt: payload?.sentAt || new Date().toISOString(),
                    }
                  : it,
              ),
            );
          }
        } catch (err) {
          // still apply optimistic update
          setAlerts((prev) =>
            prev.map((it) =>
              it.participantId === alert.participantId
                ? {
                    ...it,
                    lastContactSentAt:
                      payload?.sentAt || new Date().toISOString(),
                    lastContactReason:
                      payload?.alertReason || payload?.messageBody || null,
                    lastContactAnomalyDate: payload?.anomalyDate || null,
                    contacted: true,
                    contactedAt: payload?.sentAt || new Date().toISOString(),
                  }
                : it,
            ),
          );
        }
      },
    });
    setContactDialogOpen(true);
  };

  // Open snooze dialog for chosen alert
  function handleSnooze(alert: AlertCard) {
    setSnoozeTarget(alert);
    setSnoozePreset("1440");
    setSnoozeCustomMinutes("");
    setSnoozeDialogOpen(true);
  }

  async function performSnooze() {
    if (!isBulkSnooze && !snoozeTarget) return;
    const minutes =
      snoozePreset === "custom"
        ? parseInt(snoozeCustomMinutes || "0", 10)
        : parseInt(snoozePreset, 10);
    if (!minutes || Number.isNaN(minutes) || minutes <= 0) {
      setSnackbarMessage("Please enter a valid snooze duration in minutes.");
      setSnackbarOpen(true);
      return;
    }

    setPerformingSnooze(true);
    try {
      const token = await apiService.getAuthToken();
      const base = process.env.REACT_APP_API_URL || "";

      if (isBulkSnooze) {
        // Bulk snooze operation
        const alertsToSnooze = alerts
          .filter((a) => selectedAlerts.has(a.eventId))
          .map((a) => ({
            participantId: a.participantId,
            eventId: a.eventId,
            startDate,
            endDate,
          }));

        setAlerts((prev) => prev.filter((a) => !selectedAlerts.has(a.eventId)));

        const resp = await fetch(
          `${base}/api/anomaly-detection/alerts/bulk-snooze`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              alerts: alertsToSnooze,
              minutes,
              studyId: effectiveStudyId,
            }),
          },
        );

        if (!resp.ok) {
          const j = await resp.json().catch(() => ({}));
          throw new Error(j?.error || `HTTP ${resp.status}`);
        }

        setSnackbarMessage(
          `Snoozed ${selectedAlerts.size} alerts for ${minutes} minutes`,
        );
        setSummaryRefreshTrigger((prev) => prev + 1);
        unselectAllAlerts();
      } else {
        // Single snooze operation
        if (!snoozeTarget) return;

        setAlerts((prev) =>
          prev.filter((p) => p.eventId !== snoozeTarget.eventId),
        );

        const resp = await fetch(
          `${base}/api/anomaly-detection/alerts/snooze`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              participantId: snoozeTarget.participantId,
              eventId: snoozeTarget.eventId,
              startDate,
              endDate,
              minutes,
              studyId: effectiveStudyId,
            }),
          },
        );

        if (!resp.ok) {
          const j = await resp.json().catch(() => ({}));
          throw new Error(j?.error || `HTTP ${resp.status}`);
        }

        setSnackbarMessage(
          `Snoozed ${snoozeTarget.participantId} for ${minutes} minutes`,
        );
        setSummaryRefreshTrigger((prev) => prev + 1);
        setSnoozeTarget(null);
      }

      setSnackbarOpen(true);
      setSnoozeDialogOpen(false);
      setIsBulkSnooze(false);
    } catch (err: any) {
      // revert by re-running fetch
      setSnackbarMessage(err?.message || "Failed to snooze alert");
      setSnackbarOpen(true);
      setSnoozeDialogOpen(false);
      setSnoozeTarget(null);
      setIsBulkSnooze(false);
      runFetch();
    } finally {
      setPerformingSnooze(false);
    }
  }

  // Open ignore confirmation dialog
  function openIgnoreDialog(alert: AlertCard) {
    setIgnoreTarget(alert);
    setIgnoreNote("");
    setIgnoreDialogOpen(true);
  }

  // Perform ignore action
  async function performIgnore(alert?: AlertCard, note?: string) {
    if (!isBulkIgnore && !alert) return;

    setPerformingIgnore(true);
    try {
      const token = await apiService.getAuthToken();
      const base = process.env.REACT_APP_API_URL || "";

      if (isBulkIgnore) {
        // Bulk ignore operation
        const alertsToIgnore = alerts
          .filter((a) => selectedAlerts.has(a.eventId))
          .map((a) => ({
            participantId: a.participantId,
            eventId: a.eventId,
            startDate,
            endDate,
            anomalyDate: a.anomalyDate || null,
            reason: a.reason || null,
          }));

        // Mark all as read
        for (const a of alertsToIgnore) {
          try {
            handleMarkRead(a.eventId);
          } catch (e) {
            /* ignore */
          }
        }

        setAlerts((prev) => prev.filter((a) => !selectedAlerts.has(a.eventId)));

        const resp = await fetch(
          `${base}/api/anomaly-detection/alerts/bulk-ignore`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              alerts: alertsToIgnore,
              note: note && note.trim() ? note.trim() : null,
              studyId: effectiveStudyId,
            }),
          },
        );

        if (!resp.ok) {
          const j = await resp.json().catch(() => ({}));
          throw new Error(j?.error || `HTTP ${resp.status}`);
        }

        setSnackbarMessage(
          `Marked ${selectedAlerts.size} alerts as false positive`,
        );
        setSummaryRefreshTrigger((prev) => prev + 1);
        unselectAllAlerts();
      } else {
        // Single ignore operation
        if (!alert) return;
        // mark as read when user ignores the alert
        try {
          handleMarkRead(alert.eventId);
        } catch (e) {
          /* ignore */
        }

        setAlerts((prev) => prev.filter((p) => p.eventId !== alert.eventId));

        const resp = await fetch(
          `${base}/api/anomaly-detection/alerts/ignore`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
              participantId: alert.participantId,
              eventId: alert.eventId,
              startDate,
              endDate,
              anomalyDate: alert.anomalyDate || null,
              reason: alert.reason || null,
              studyId: effectiveStudyId,
            }),
          },
        );

        if (!resp.ok) {
          const j = await resp.json().catch(() => ({}));
          throw new Error(j?.error || `HTTP ${resp.status}`);
        }

        // If a note was provided, save it to participants_alerts.researcher_notes via backend endpoint
        if (note && note.trim()) {
          try {
            const noteResp = await fetch(
              `${base}/api/anomaly-detection/alerts/researcher-note`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                body: JSON.stringify({
                  participantId: alert.participantId,
                  eventId: alert.eventId,
                  note: note.trim(),
                }),
              },
            );

            if (!noteResp.ok) {
              // Silently handle error
            }
          } catch (err) {
            // Silently handle error
          }
        }

        setSnackbarMessage(
          `Ignored ${alert.participantId} — marked as false positive`,
        );
        setSummaryRefreshTrigger((prev) => prev + 1);
        setIgnoreTarget(null);
      }

      setSnackbarOpen(true);
      setIgnoreDialogOpen(false);
      setIsBulkIgnore(false);
    } catch (err: any) {
      setSnackbarMessage(err?.message || "Failed to ignore alert");
      setSnackbarOpen(true);
      setIgnoreDialogOpen(false);
      setIgnoreTarget(null);
      setIsBulkIgnore(false);
      // revert by re-running fetch
      runFetch();
    } finally {
      setPerformingIgnore(false);
    }
  }

  // Perform bulk contact action
  // sends emails to all selected participants
  async function performBulkContact() {
    if (selectedAlerts.size === 0) return;

    setPerformingBulkContact(true);
    try {
      const token = await apiService.getAuthToken();
      const base = process.env.REACT_APP_API_URL || "";

      // Collect all selected alerts with their info
      const alertsToContact = alerts
        .filter((a) => selectedAlerts.has(a.eventId))
        .map((a) => ({
          participantId: a.participantId,
          eventId: a.eventId,
          alertType: a.alertType,
          alertReason: a.reason,
          startDate,
          endDate,
          anomalyDate: a.anomalyDate || null,
          contactTemplate: a.contactTemplate || null,
        }));

      const resp = await fetch(
        `${base}/api/anomaly-detection/alerts/bulk-contact`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            alerts: alertsToContact,
            studyId: propStudyId,
          }),
        },
      );

      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${resp.status}`);
      }

      const result = await resp.json();

      // Fetch updated contact info for all contacted participants
      const contactedParticipantIds = alertsToContact.map(
        (a) => a.participantId,
      );
      try {
        const contacts = await fetchLastContactsForParticipants(
          contactedParticipantIds,
        );
        if (Array.isArray(contacts) && contacts.length > 0) {
          // Update alerts with new contact info
          setAlerts((prev) =>
            prev.map((alert) => {
              const contact = contacts.find(
                (c: any) => c.participantId === alert.participantId,
              );
              if (contact) {
                return {
                  ...alert,
                  lastContactSentAt: contact.sentAt || null,
                  lastContactReason:
                    contact.alertReason || contact.messageBody || null,
                  lastContactAnomalyDate: contact.anomalyDate || null,
                  contacted: true,
                  contactedAt: contact.sentAt || null,
                };
              }
              return alert;
            }),
          );
        }
      } catch (err) {
        // Silently handle error
      }

      setSnackbarMessage(
        `Contacted ${
          result.summary?.succeeded || selectedAlerts.size
        } participant${result.summary?.succeeded !== 1 ? "s" : ""}`,
      );
      setSummaryRefreshTrigger((prev) => prev + 1);
      unselectAllAlerts();
      setBulkContactConfirmDialogOpen(false);
      setSnackbarOpen(true);
    } catch (err: any) {
      setSnackbarMessage(err?.message || "Failed to contact participants");
      setSnackbarOpen(true);
      setBulkContactConfirmDialogOpen(false);
      runFetch();
    } finally {
      setPerformingBulkContact(false);
    }
  }

  // Bulk selection handlers
  function toggleSelectAlert(eventId: string) {
    setSelectedAlerts((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(eventId)) {
        newSet.delete(eventId);
      } else {
        newSet.add(eventId);
      }
      return newSet;
    });
  }

  function selectAllAlerts() {
    const allEventIds = new Set(alerts.map((a) => a.eventId));
    setSelectedAlerts(allEventIds);
  }

  function unselectAllAlerts() {
    setSelectedAlerts(new Set());
  }

  async function contactAllSelected() {
    // Open bulk contact confirmation dialog
    setBulkContactConfirmDialogOpen(true);
  }

  async function falsePositiveAllSelected() {
    // Open ignore dialog in bulk mode
    setIgnoreNote("");
    setIsBulkIgnore(true);
    setIgnoreTarget(null); // Not needed for bulk, but keeping for state consistency
    setIgnoreDialogOpen(true);
  }

  async function snoozeAllSelected() {
    // Open snooze dialog in bulk mode
    setSnoozePreset("1440");
    setSnoozeCustomMinutes("");
    setIsBulkSnooze(true);
    setSnoozeTarget(null); // Not needed for bulk, but keeping for state consistency
    setSnoozeDialogOpen(true);
  }

  async function handleResolve(alert: AlertCard) {
    // mark as read when user resolves the alert (optimistic)
    try {
      handleMarkRead(alert.eventId);
    } catch (e) {
      /* ignore */
    }
    try {
      // optimistic remove by participantId (top-event based)
      setAlerts((prev) => prev.filter((a) => a.eventId !== alert.eventId));

      const token = await apiService.getAuthToken();
      const base = process.env.REACT_APP_API_URL || "";
      const resp = await fetch(`${base}/api/anomaly-detection/alerts/resolve`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          participantId: alert.participantId,
          eventId: alert.eventId,
          startDate,
          endDate,
          anomalyDate: alert.anomalyDate,
          reason: alert.reason || null,
          studyId: effectiveStudyId,
        }),
      });

      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${resp.status}`);
      }

      setSnackbarMessage(`Resolved alert for ${alert.participantId}`);
      setSummaryRefreshTrigger((prev) => prev + 1);
      setSnackbarOpen(true);
    } catch (err: any) {
      setSnackbarMessage(err?.message || "Failed to resolve alert");
      setSnackbarOpen(true);
      // revert by re-fetching alerts
      runFetch();
    }
  }

  // Restore state from sessionStorage
  useEffect(() => {
    const raw = sessionStorage.getItem(storageKey);
    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed && typeof parsed === "object") {
          if (parsed.startDate) setStartDate(parsed.startDate);
          if (parsed.endDate) setEndDate(parsed.endDate);
          if (Array.isArray(parsed.alerts) && parsed.alerts.length > 0) {
            setAlerts(parsed.alerts);
            setHasInitiallyLoaded(true);
          }
        }
      } catch {}
    }
  }, [storageKey]);

  // Initialize default dates (to match cron job: end = yesterday, start = yesterday - 14 days)
  useEffect(() => {
    if (!startDate || !endDate) {
      // Use UTC date arithmetic to compute yesterday and the start date
      const now = new Date();
      const utcToday = new Date(
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()),
      );
      const utcYesterday = new Date(utcToday);
      utcYesterday.setUTCDate(utcYesterday.getUTCDate() - 1);

      const utcStart = new Date(utcYesterday);
      utcStart.setUTCDate(utcStart.getUTCDate() - (DEFAULT_N_DAYS - 1)); // 15-day window ending yesterday

      setStartDate((s) => s || utcStart.toISOString().slice(0, 10));
      setEndDate((e) => e || utcYesterday.toISOString().slice(0, 10));
    }
  }, [startDate, endDate]);

  useEffect(() => {
    if (
      notificationRef &&
      effectiveStudyId &&
      highlightedParticipants.size > 0
    ) {
      trackNotificationClickThrough(
        notificationRef,
        effectiveStudyId,
        Array.from(highlightedParticipants),
        {
          filter: filterOption,
          sort: sortOption,
        },
      ).catch(() => {
        // Silently handle error
      });
    }
  }, [
    notificationRef,
    effectiveStudyId,
    highlightedParticipants,
    filterOption,
    sortOption,
  ]);

  // Persist state to sessionStorage
  useEffect(() => {
    const snapshot = JSON.stringify({ startDate, endDate, alerts });
    sessionStorage.setItem(storageKey, snapshot);
  }, [storageKey, startDate, endDate, alerts]);

  function formatSentAt(sentAt?: string | null) {
    if (!sentAt) return null;
    const d = new Date(sentAt);
    if (isNaN(d.getTime())) return sentAt;
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    const yyyy = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, "0");
    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    if (hours === 0) hours = 12;
    return `${mm}-${dd}-${yyyy} ${hours}:${minutes} ${ampm}`;
  }

  // Fetch last contact entries for a list of participantIds
  async function fetchLastContactsForParticipants(participantIds: string[]) {
    try {
      if (!effectiveStudyId || participantIds.length === 0) return [];
      const token = await apiService.getAuthToken();
      const base = process.env.REACT_APP_API_URL || "";
      const resp = await fetch(
        `${base}/api/anomaly-detection/alerts/contacts/last`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({ studyId: effectiveStudyId, participantIds }),
        },
      );
      if (!resp.ok) {
        return [];
      }
      const json = await resp.json();
      return Array.isArray(json.contacts) ? json.contacts : [];
    } catch (err) {
      return [];
    }
  }

  // Fetch read-alerts for the current researcher + range and apply to current alerts array.
  async function fetchReadAlertsAndApply(currentAlerts: AlertCard[]) {
    try {
      const token = await apiService.getAuthToken();
      const base = process.env.REACT_APP_API_URL || "";
      const resp = await fetch(`${base}/api/anomaly-detection/alerts/read`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ studyId: effectiveStudyId, startDate, endDate }),
      });
      if (!resp.ok) return;
      const json = await resp.json();
      if (!json?.readAlerts) return;
      const readSet = new Set(json.readAlerts.map((r: any) => r.eventId));
      const updated = currentAlerts.map((a) =>
        readSet.has(a.eventId) ? { ...a, unread: false } : a,
      );
      setAlerts(updated);
    } catch (e) {
      // Silently handle error
    }
  }

  async function markReadBackend(eventId: string) {
    try {
      const token = await apiService.getAuthToken();
      const base = process.env.REACT_APP_API_URL || "";
      const resp = await fetch(
        `${base}/api/anomaly-detection/alerts/mark-read`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            eventId,
            studyId: effectiveStudyId,
            startDate,
            endDate,
          }),
        },
      );
      if (!resp.ok) {
        const j = await resp.json().catch(() => ({}));
        throw new Error(j?.error || `HTTP ${resp.status}`);
      }
      return true;
    } catch (err) {
      return false;
    }
  }

  const runFetch = useCallback(async () => {
    if (!effectiveStudyId || !validRange({ startDate, endDate })) return;
    setLoading(true);
    setError("");
    setProgressMessage("Initializing...");

    try {
      const token = await apiService.getAuthToken();
      const base = process.env.REACT_APP_API_URL || "";
      const url = `${base}/api/anomaly-detection/alerts/progress?studyId=${encodeURIComponent(
        effectiveStudyId,
      )}&startDate=${encodeURIComponent(
        startDate,
      )}&endDate=${encodeURIComponent(endDate)}`;

      const response = await fetch(url, {
        headers: {
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let buffer = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));

              if (data.type === "progress") {
                setProgressMessage(data.message || "Processing...");
              } else if (data.type === "complete") {
                const result = data.result;
                if (result && result.alerts) {
                  const normalized: AlertCard[] = (
                    Array.isArray(result.alerts) ? result.alerts : []
                  )
                    .map((a: any) => ({
                      eventId: a.eventId || "",
                      // Use undefined when API does not provide alert_id so we don't send empty strings
                      alertId: a.alert_id ?? undefined,
                      participantId: a.participantId || "",
                      priorityScore:
                        typeof a.priorityScore === "number"
                          ? a.priorityScore
                          : 0,
                      alertType: a.alertType || "",
                      reason: a.reason || "",
                      contactedAt: a.contactedAt || null,
                      contacted:
                        typeof a.contacted === "boolean" ? a.contacted : false,
                      unread: typeof a.unread === "boolean" ? a.unread : true,
                      detectedAt: a.detectedAt || null,
                      anomalyDate: a.anomalyDate || null,
                      totalAnomalies: a.totalAnomalies || 1,
                      contactTemplate: a.contactTemplate || "",
                      lastContactSentAt: null,
                      lastContactReason: null,
                      lastSyncDate: a.lastSyncDate || null,
                      daysWithoutData: a.daysWithoutData || null,
                      alertStreak14d: a.alertStreak14d || null,
                      preAlertAdherence7d: a.preAlertAdherence7d || null,
                      dateRangeAvgAdherence:
                        typeof a.dateRangeAvgAdherence === "number"
                          ? a.dateRangeAvgAdherence
                          : undefined,
                      events: Array.isArray(a.events)
                        ? a.events.map((evt: any) => ({
                            eventId: evt.eventId || "",
                            alertType: evt.alertType || "",
                            reason: evt.reason || "",
                            priorityScore:
                              typeof evt.priorityScore === "number"
                                ? evt.priorityScore
                                : 0,
                            detectedAt: evt.detectedAt || null,
                            anomalyDate: evt.anomalyDate || null,
                            contactTemplate: evt.contactTemplate || "",
                            email: evt.email || "",
                          }))
                        : [],
                    }))
                    .filter((r: AlertCard) => r.eventId && r.participantId);

                  // fetch last contacts for participants and merge
                  const participantIds = normalized.map((n) => n.participantId);
                  const contacts =
                    await fetchLastContactsForParticipants(participantIds);

                  const contactMap: Record<string, any> = {};
                  contacts.forEach((c: any) => {
                    if (c && c.participantId) contactMap[c.participantId] = c;
                  });

                  const merged = normalized.map((n) => {
                    const c = contactMap[n.participantId];
                    if (c) {
                      return {
                        ...n,
                        lastContactSentAt: c.sentAt || null,
                        lastContactReason:
                          c.alertReason || c.messageBody || null,
                        lastContactAnomalyDate: c.anomalyDate || null,
                      };
                    }
                    return n;
                  });

                  setAlerts(merged);
                  setHasInitiallyLoaded(true);
                  setSummaryRefreshTrigger((prev) => prev + 1);

                  // Apply persisted read markers
                  fetchReadAlertsAndApply(merged);
                }
              } else if (data.type === "error") {
                setError(data.message || data.error || "Failed to load alerts");
              }
            } catch (parseErr) {
              // Silently handle parse error
            }
          }
        }
      }
    } catch (e: any) {
      setError(e?.message || "Failed to load alerts");
    } finally {
      setLoading(false);
      setProgressMessage("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveStudyId, startDate, endDate]);

  useEffect(() => {
    if (startDate && endDate && !hasInitiallyLoaded) {
      runFetch();
    }
  }, [startDate, endDate, hasInitiallyLoaded, runFetch]);

  const handleMarkRead = async (eventId: string) => {
    // Optimistic UI update
    setAlerts((prev) =>
      prev.map((a) => (a.eventId === eventId ? { ...a, unread: false } : a)),
    );

    const ok = await markReadBackend(eventId);
    if (!ok) {
      // revert on error
      setAlerts((prev) =>
        prev.map((a) => (a.eventId === eventId ? { ...a, unread: true } : a)),
      );
    }
  };

  const toggleExpand = (participantId: string) => {
    setExpandedCards((prev) => {
      const next = new Set(prev);
      if (next.has(participantId)) {
        next.delete(participantId);
      } else {
        next.add(participantId);
      }
      return next;
    });
  };

  return (
    <Box sx={{ px: 2, py: 2 }}>
      {/* Adherence Monitor Summary Widget */}
      {effectiveStudyId && (
        <AdherenceMonitorSummary
          studyId={effectiveStudyId}
          refreshTrigger={summaryRefreshTrigger}
        />
      )}

      <Paper sx={{ p: 2 }}>
        <Typography
          variant="h5"
          sx={{
            mb: 1,
            textAlign: "center",
            fontWeight: 600,
          }}
        >
          Anomaly Detected from{" "}
          {startDate && endDate ? formatRangeTitle(startDate, endDate) : "..."}
        </Typography>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{
            display: "block",
            textAlign: "center",
            mb: 3,
            fontStyle: "italic",
          }}
        >
          Showing the highest-priority anomaly for each participant. Additional
          anomalies are available via the dropdown when applicable.
          <br />
          <b>Note:</b> Displayed dates may differ by one day due to timezone
          differences.
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "center",
            mb: 3,
          }}
        >
          <DateRangeControl key={effectiveStudyId} value={{ startDate: startDate || '', endDate: endDate || '' }}
              onChange={range => { setStartDate(range.startDate); setEndDate(range.endDate); }}
              startLabel="Start Date" endLabel="End Date" disabled={loading} maxDate={yesterdayISO} />
          <Button variant="contained" onClick={runFetch} disabled={loading || !validRange({ startDate, endDate })}>
            {loading ? <CircularProgress size={18} /> : "Run"}
          </Button>
        </Box>

        {/* Sort and Filter Controls */}
        {alerts.length > 0 && (
          <Box
            sx={{
              display: "flex",
              gap: 2,
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "flex-start",
              mb: 3,
            }}
          >
            <FormControl
              size="small"
              sx={{
                minWidth: 150,
                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "primary.main",
                  },
              }}
            >
              <InputLabel>Sort</InputLabel>
              <Select
                value={sortOption}
                onChange={(e) => {
                  const newSort = e.target.value as SortOption;
                  setSortOption(newSort);
                  updateAlertsURL({
                    study_id: effectiveStudyId,
                    filter: filterOption,
                    sort: newSort,
                    highlight: Array.from(highlightedParticipants),
                    notification_ref: notificationRef,
                    start_date: startDate,
                    end_date: endDate,
                  });
                }}
                label="Sort"
              >
                <MenuItem value="adherence_asc">
                  Adherence (Low to High)
                </MenuItem>
                <MenuItem value="date_desc">Date (Newest First)</MenuItem>
                <MenuItem value="participant_asc">
                  Participant ID (A-Z)
                </MenuItem>
              </Select>
            </FormControl>

            <FormControl
              size="small"
              sx={{
                minWidth: 150,
                "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: "primary.main",
                  },
              }}
            >
              <InputLabel>Filter</InputLabel>
              <Select
                value={filterOption}
                onChange={(e) => {
                  const newFilter = e.target.value as FilterOption;
                  setFilterOption(newFilter);
                  updateAlertsURL({
                    study_id: effectiveStudyId,
                    filter: newFilter,
                    sort: sortOption,
                    highlight: Array.from(highlightedParticipants),
                    notification_ref: notificationRef,
                    start_date: startDate,
                    end_date: endDate,
                  });
                }}
                label="Filter"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        {/* Notification Banner */}
        {notificationBannerVisible &&
          notificationRef &&
          highlightedParticipants.size > 0 && (
            <Box
              sx={{
                mt: 2,
                mb: 2,
                p: 2,
                bgcolor: "info.lighter",
                border: "1px solid",
                borderColor: "info.main",
                borderRadius: 1,
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 2,
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="center"
                sx={{ flex: 1 }}
              >
                <Typography variant="body2" color="info.dark">
                  <strong>Email Notification:</strong> You arrived from an email
                  notification about{" "}
                  <strong>{highlightedParticipants.size}</strong> high-risk{" "}
                  {highlightedParticipants.size === 1
                    ? "participant"
                    : "participants"}
                  . Rows highlighted below.
                </Typography>
              </Stack>
              <IconButton
                size="small"
                onClick={async () => {
                  setNotificationBannerVisible(false);
                  await recordNotificationBannerDismissal(
                    notificationRef,
                    effectiveStudyId || "",
                  );
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}

        {loading && alerts.length > 0 && (
          <Box
            sx={{
              mt: 2,
              mb: 2,
              p: 2,
              bgcolor: "action.hover",
              borderRadius: 1,
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <CircularProgress size={20} />
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Executing anomaly detection. This might take a while...
                </Typography>
                {progressMessage && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", mt: 0.5 }}
                  >
                    {progressMessage}
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        )}

        {error && (
          <Typography color="error" sx={{ textAlign: "center", mb: 2 }}>
            {error}
          </Typography>
        )}

        {loading && alerts.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 4 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Executing anomaly detection. This might take a while...
            </Typography>
            {progressMessage && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: "block" }}
              >
                {progressMessage}
              </Typography>
            )}
          </Box>
        ) : alerts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No alerts found for the selected range.
          </Typography>
        ) : (
          <>
            {/* Bulk Action Bar */}
            {selectedAlerts.size > 0 && (
              <Paper
                sx={{
                  mb: 2,
                  p: 2,
                  bgcolor: "background.paper",
                  border: "1px solid",
                  borderColor: "divider",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ fontWeight: "500", color: "text.primary" }}
                >
                  {selectedAlerts.size} alert
                  {selectedAlerts.size !== 1 ? "s" : ""} selected
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={contactAllSelected}
                  >
                    Contact All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={falsePositiveAllSelected}
                  >
                    Ignore All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={snoozeAllSelected}
                  >
                    Snooze All
                  </Button>
                </Stack>
                <Stack direction="row" spacing={1}>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={selectAllAlerts}
                  >
                    Select All
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={unselectAllAlerts}
                  >
                    Clear Selection
                  </Button>
                </Stack>
              </Paper>
            )}

            <Stack spacing={2}>
              {procesAlerts(alerts, sortOption, filterOption).map((a) => {
                const displayAdherence = a.dateRangeAvgAdherence ?? a.priorityScore ?? 0;
                const color = scoreColor(displayAdherence);
                const isExpanded = expandedCards.has(a.participantId);
                const hasMultiple = a.totalAnomalies > 1;
                const isHighlighted = highlightedParticipants.has(
                  a.participantId,
                );

                return (
                  <Card
                    key={a.eventId}
                    variant="outlined"
                    sx={{
                      position: "relative",
                      border: "2px solid",
                      borderColor: isHighlighted ? "info.main" : "divider",
                      borderRadius: 2,
                      transition: "all 0.2s ease",
                      bgcolor: isHighlighted
                        ? "rgba(25, 118, 210, 0.05)"
                        : "transparent",
                      "&:hover": {
                        borderColor: isHighlighted
                          ? "info.main"
                          : "primary.main",
                        boxShadow: isHighlighted
                          ? "0 0 12px rgba(25, 118, 210, 0.15)"
                          : 1,
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "flex-start",
                        }}
                      >
                        {/* Checkbox */}
                        <Checkbox
                          checked={selectedAlerts.has(a.eventId)}
                          onChange={() => toggleSelectAlert(a.eventId)}
                          sx={{ mt: 0.5 }}
                        />
                        {/* Content */}
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "flex-start",
                            flex: 1,
                          }}
                        >
                          {/* Left side - Participant info */}
                          <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                            <Box sx={{ mb: 2 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontWeight: 500,
                                  mb: 0.5,
                                  display: "block",
                                }}
                              >
                                Participant ID
                              </Typography>
                              <Stack
                                direction="row"
                                spacing={2}
                                alignItems="center"
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontFamily: "monospace",
                                    fontWeight: 600,
                                    fontSize: "1.1rem",
                                  }}
                                >
                                  {a.participantId}
                                </Typography>
                                <Chip
                                  label={`Avg Adherence: ${displayAdherence.toFixed(1)}%`}
                                  color={color as any}
                                  size="small"
                                  sx={{ fontWeight: 600, height: 24 }}
                                />
                              </Stack>

                              {a.anomalyDate && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", mt: 0.25 }}
                                >
                                  Anomaly Date: {formatMMDDYYYY(a.anomalyDate)}
                                </Typography>
                              )}
                            </Box>

                            <Box sx={{ mb: 1.5 }}>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  fontWeight: 500,
                                  mb: 0.5,
                                  display: "block",
                                }}
                              >
                                Reason for Alert
                              </Typography>
                              <Typography
                                variant="body1"
                                sx={{
                                  color: "text.primary",
                                  lineHeight: 1.6,
                                }}
                              >
                                {a.reason || a.alertType || "—"}
                              </Typography>
                            </Box>

                            <Box>
                              {/* <Typography variant="caption" color="text.secondary">
                            Last Contact: {lastContact}
                          </Typography> */}
                              {a.lastContactSentAt && (
                                <Typography
                                  variant="caption"
                                  color="text.secondary"
                                  sx={{ display: "block", mt: 0.5 }}
                                >
                                  Contacted: {formatSentAt(a.lastContactSentAt)}
                                  {a.lastContactAnomalyDate
                                    ? ` (Anomaly: ${formatMMDDYYYY(
                                        a.lastContactAnomalyDate,
                                      )})`
                                    : ""}
                                  {a.lastContactReason
                                    ? ` — ${a.lastContactReason}`
                                    : ""}
                                </Typography>
                              )}
                            </Box>

                            {/* Alert Metadata Section - Two column grid */}
                            {(a.lastSyncDate ||
                              a.daysWithoutData !== null ||
                              a.alertStreak14d ||
                              a.preAlertAdherence7d) && (
                              <Box
                                sx={{
                                  mt: 2,
                                  pt: 1.5,
                                  borderTop: "1px solid",
                                  borderColor: "divider",
                                }}
                              >
                                <Box
                                  sx={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 1fr",
                                    gap: 1,
                                  }}
                                >
                                  {a.lastSyncDate && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Last Sync:{" "}
                                      <strong>
                                        {formatMMDDYYYY(a.lastSyncDate)}
                                      </strong>
                                    </Typography>
                                  )}
                                  {a.daysWithoutData !== null && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Days without data:{" "}
                                      <strong>{a.daysWithoutData}d</strong>
                                    </Typography>
                                  )}
                                  {a.alertStreak14d && (
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      Alert streak (14d):{" "}
                                      <strong>{a.alertStreak14d}</strong>
                                    </Typography>
                                  )}
                                  {a.preAlertAdherence7d !== null &&
                                    a.preAlertAdherence7d !== undefined && (
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        Pre-alert adherence (7d):{" "}
                                        <strong>
                                          {Math.round(a.preAlertAdherence7d)}%
                                        </strong>
                                      </Typography>
                                    )}
                                </Box>
                              </Box>
                            )}

                            {/* Note for improved adherence after alert */}
                            {a.lastSyncDate &&
                              a.anomalyDate &&
                              new Date(a.lastSyncDate) >
                                new Date(a.anomalyDate) && (
                                <Box
                                  sx={{
                                    mt: 1.5,
                                    p: 1,
                                    borderLeft: "3px solid",
                                    borderColor: "success.main",
                                    borderRadius: 0.5,
                                    display: "inline-block",
                                    bgcolor: "transparent",
                                  }}
                                >
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      color: "success.dark",
                                      fontWeight: 500,
                                    }}
                                  >
                                    ✓ Adherence improved after alert. Consider
                                    marking as resolved.
                                  </Typography>
                                </Box>
                              )}

                            {/* Dropdown for all anomalies */}

                            {hasMultiple && (
                              <Box sx={{ mt: 2 }}>
                                <Button
                                  size="small"
                                  onClick={() => toggleExpand(a.participantId)}
                                  endIcon={
                                    <ExpandMoreIcon
                                      sx={{
                                        transform: isExpanded
                                          ? "rotate(180deg)"
                                          : "rotate(0deg)",
                                        transition: "transform 0.2s",
                                      }}
                                    />
                                  }
                                  sx={{ textTransform: "none" }}
                                >
                                  All Anomalies ({a.totalAnomalies})
                                </Button>
                                <Collapse in={isExpanded} timeout="auto">
                                  <List
                                    dense
                                    sx={{
                                      mt: 1,
                                      width: "100%",
                                      maxWidth: "min(100%, 560px)",
                                    }}
                                  >
                                    {a.events.map((evt, idx) => (
                                      <ListItem
                                        key={evt.eventId}
                                        sx={{
                                          border: "1px solid",
                                          borderColor: "divider",
                                          borderRadius: 1,
                                          mb: 0.5,
                                          bgcolor: "background.default",
                                          px: 1,
                                          py: 0.75,
                                        }}
                                      >
                                        <ListItemText
                                          primary={
                                            <Stack
                                              direction="row"
                                              spacing={1}
                                              alignItems="center"
                                              sx={{ minWidth: 0 }}
                                            >
                                              <Chip
                                                label={`Adherence: ${evt.priorityScore.toFixed(
                                                  1,
                                                )}%`}
                                                color={
                                                  scoreColor(
                                                    evt.priorityScore,
                                                  ) as any
                                                }
                                                size="small"
                                                sx={{ height: 20 }}
                                              />
                                              <Typography
                                                variant="body2"
                                                sx={{ minWidth: 0 }}
                                              >
                                                {evt.reason || evt.alertType}
                                              </Typography>
                                            </Stack>
                                          }
                                          secondary={
                                            evt.anomalyDate
                                              ? `Date: ${formatMMDDYYYY(
                                                  evt.anomalyDate,
                                                )}`
                                              : undefined
                                          }
                                        />
                                      </ListItem>
                                    ))}
                                  </List>
                                </Collapse>
                              </Box>
                            )}
                          </Box>

                          {/* Right side - Actions & status */}
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-end",
                              gap: 1.5,
                            }}
                          >
                            <Stack
                              direction="row"
                              spacing={1}
                              alignItems="center"
                            >
                              {a.unread ? (
                                <>
                                  <Chip
                                    icon={<DotIcon sx={{ fontSize: 12 }} />}
                                    label="New"
                                    size="small"
                                    sx={{
                                      height: 24,
                                      fontSize: "0.75rem",
                                      fontWeight: 600,
                                      animation:
                                        "newChipPulse 3s ease-in-out infinite",
                                      "@keyframes newChipPulse": {
                                        "0%, 100%": {
                                          backgroundColor: "transparent",
                                          borderColor: "error.main",
                                          color: "error.main",
                                          border: "1px solid",
                                          "& .MuiChip-icon": {
                                            color: "error.main",
                                          },
                                        },
                                        "50%": {
                                          backgroundColor: "error.main",
                                          borderColor: "error.main",
                                          color: "white",
                                          border: "1px solid",
                                          "& .MuiChip-icon": {
                                            color: "white",
                                          },
                                        },
                                      },
                                    }}
                                  />
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    startIcon={<TickIcon />}
                                    onClick={() => handleMarkRead(a.eventId)}
                                    sx={{
                                      textTransform: "none",
                                      fontSize: "0.75rem",
                                      minWidth: "auto",
                                      px: 1.5,
                                      py: 0.5,
                                      justifyContent: "center",
                                    }}
                                  >
                                    Mark as Read
                                  </Button>
                                </>
                              ) : (
                                <Chip label="Read" size="small" />
                              )}
                            </Stack>

                            <Stack direction="column" spacing={1}>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<VisibilityIcon />}
                                sx={{
                                  textTransform: "none",
                                  justifyContent: "center",
                                  minWidth: 140,
                                }}
                                onClick={() => {
                                  navigate(`/researcher/participant-profile/${encodeURIComponent(a.participantId)}/${encodeURIComponent(effectiveStudyId || '')}`);
                                }}
                              >
                                View Profile
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<EmailIcon />}
                                sx={{
                                  textTransform: "none",
                                  justifyContent: "center",
                                  minWidth: 140,
                                }}
                                onClick={() => {
                                  handleMarkRead(a.eventId);
                                  handleContactNow(a);
                                }}
                              >
                                Contact
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                startIcon={<SnoozeIcon />}
                                sx={{
                                  textTransform: "none",
                                  justifyContent: "center",
                                  minWidth: 140,
                                }}
                                onClick={() => {
                                  handleMarkRead(a.eventId);
                                  handleSnooze(a);
                                }}
                              >
                                Snooze
                              </Button>
                              <Stack direction="row" spacing={1}>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  startIcon={<IgnoreIcon />}
                                  sx={{
                                    textTransform: "none",
                                    justifyContent: "center",
                                    minWidth: 68,
                                    flex: 1,
                                    fontSize: "0.75rem",
                                  }}
                                  onClick={() => {
                                    handleMarkRead(a.eventId);
                                    openIgnoreDialog(a);
                                  }}
                                  title="Ignore this alert"
                                >
                                  Ignore
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                  sx={{ textTransform: "none", minWidth: 100 }}
                                  onClick={() => handleResolve(a)}
                                >
                                  Resolve
                                </Button>
                              </Stack>
                            </Stack>
                            {/* Survey prompt with normal text size, wraps to 2-3 rows */}
                            <Paper
                              variant="outlined"
                              sx={{
                                mt: 1,
                                p: 1,
                                borderRadius: 1,
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.5,
                                bgcolor: "background.default",
                                cursor: "pointer",
                                width: 400,
                                "&:hover": { boxShadow: 1 },
                              }}
                              onClick={() => {
                                setSurveyDialogAlert(a);
                                setSurveyDialogOpen(true);
                              }}
                            >
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "flex-start",
                                  gap: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  sx={{ lineHeight: 1.4 }}
                                >
                                  Kindly answer a few questions before taking an
                                  action — this helps us gather scientific data.
                                </Typography>
                                <Typography
                                  component="a"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSurveyDialogAlert(a);
                                    setSurveyDialogOpen(true);
                                  }}
                                  sx={{
                                    color: "primary.main",
                                    fontWeight: 700,
                                    textDecoration: "none",
                                    cursor: "pointer",
                                    "&:hover": { textDecoration: "underline" },
                                    fontSize: "0.875rem",
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                  }}
                                >
                                  Proceed
                                </Typography>
                              </Box>
                            </Paper>
                          </Box>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                );
              })}
            </Stack>
          </>
        )}
      </Paper>
      {contactDialogOpen && contactDialogProps && (
        <AlertContactDialog {...contactDialogProps} />
      )}

      {surveyDialogOpen && surveyDialogAlert && (
        <ActionSurveyDialog
          open={surveyDialogOpen}
          alert={{
            id: surveyDialogAlert.eventId,
            alertId: surveyDialogAlert.alertId,
            participantId: surveyDialogAlert.participantId,
            alertType: surveyDialogAlert.alertType,
            reason: surveyDialogAlert.reason,
            lastSyncDate: surveyDialogAlert.lastSyncDate,
            preAlertAdherence7d: surveyDialogAlert.preAlertAdherence7d,
          }}
          onClose={() => {
            setSurveyDialogOpen(false);
            setSurveyDialogAlert(null);
          }}
          onSave={(payload: any) => {
            // Child dialog shows its own confirmation snackbar — avoid duplicate toasts here.
            // Future: handle payload if parent-level updates are needed (e.g., refresh list).
          }}
        />
      )}

      {/* Ignore (False Positive) confirmation dialog */}
      <Dialog
        open={ignoreDialogOpen}
        onClose={() => {
          if (!performingIgnore) {
            setIgnoreDialogOpen(false);
            setIsBulkIgnore(false);
            setIgnoreTarget(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {isBulkIgnore ? "Mark as false positive?" : "Mark as false positive?"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isBulkIgnore
              ? `Are you sure you want to mark ${selectedAlerts.size} alert${
                  selectedAlerts.size !== 1 ? "s" : ""
                } as false positive? This will mark the alert${
                  selectedAlerts.size !== 1 ? "s" : ""
                } as ignored for the participant${
                  selectedAlerts.size !== 1 ? "s" : ""
                }. This helps improve future detection.`
              : "Are you sure you want to mark this alert as a false positive? This will mark the alert as ignored for this participant. This helps improve future detection."}
          </Typography>
          <TextField
            fullWidth
            label="Optional note to record"
            placeholder="Add a note for other researchers (optional)"
            multiline
            minRows={3}
            value={ignoreNote}
            onChange={(e) => setIgnoreNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIgnoreDialogOpen(false);
              setIsBulkIgnore(false);
              setIgnoreTarget(null);
            }}
            disabled={performingIgnore}
          >
            Cancel
          </Button>
          <Button
            onClick={() =>
              performIgnore(
                isBulkIgnore ? undefined : ignoreTarget || undefined,
                ignoreNote,
              )
            }
            variant="contained"
            disabled={performingIgnore}
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            {performingIgnore ? (
              <CircularProgress size={18} />
            ) : (
              "Mark as False Positive"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk contact confirmation dialog */}
      <Dialog
        open={bulkContactConfirmDialogOpen}
        onClose={() => {
          if (!performingBulkContact) {
            setBulkContactConfirmDialogOpen(false);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Contact participants?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Are you sure you want to contact {selectedAlerts.size} participant
            {selectedAlerts.size !== 1 ? "s" : ""}? This will send email
            {selectedAlerts.size !== 1 ? "s" : ""} using the default template
            {selectedAlerts.size !== 1 ? "s" : ""}.
          </Typography>
          <MuiAlert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2">
              <strong>Note:</strong> Each participant will receive an email
              using their respective alert's default template. Templates will be
              automatically selected based on the alert type.
            </Typography>
          </MuiAlert>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setBulkContactConfirmDialogOpen(false)}
            disabled={performingBulkContact}
          >
            Cancel
          </Button>
          <Button
            onClick={performBulkContact}
            variant="contained"
            disabled={performingBulkContact}
            sx={{
              bgcolor: "primary.main",
              color: "primary.contrastText",
              "&:hover": { bgcolor: "primary.dark" },
            }}
          >
            {performingBulkContact ? (
              <CircularProgress size={18} />
            ) : (
              "Contact All"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snooze dialog (replace prompt) */}
      <Dialog
        open={snoozeDialogOpen}
        onClose={() => {
          if (!performingSnooze) {
            setSnoozeDialogOpen(false);
            setIsBulkSnooze(false);
            setSnoozeTarget(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {isBulkSnooze
            ? `Snooze ${selectedAlerts.size} Alerts`
            : "Snooze Alert"}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {isBulkSnooze
              ? `Snooze alerts for ${selectedAlerts.size} participant${
                  selectedAlerts.size !== 1 ? "s" : ""
                }. Choose a preset duration or enter a custom number of minutes.`
              : "Snooze alerts for this participant. Choose a preset duration or enter a custom number of minutes."}
          </Typography>

          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="snooze-preset-label">Preset</InputLabel>
            <Select
              labelId="snooze-preset-label"
              value={snoozePreset}
              label="Preset"
              onChange={(e) => setSnoozePreset(String(e.target.value))}
            >
              <MenuItem value="60">1 hour</MenuItem>
              <MenuItem value="240">4 hours</MenuItem>
              <MenuItem value="1440">24 hours</MenuItem>
              <MenuItem value="10080">7 days</MenuItem>
              <MenuItem value="custom">Custom (minutes)</MenuItem>
            </Select>
          </FormControl>

          {snoozePreset === "custom" && (
            <TextField
              fullWidth
              label="Minutes"
              type="number"
              value={snoozeCustomMinutes}
              onChange={(e) => setSnoozeCustomMinutes(e.target.value)}
              placeholder="Enter minutes (e.g. 180)"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSnoozeDialogOpen(false);
              setIsBulkSnooze(false);
              setSnoozeTarget(null);
            }}
            disabled={performingSnooze}
          >
            Cancel
          </Button>
          <Button
            onClick={performSnooze}
            variant="contained"
            disabled={performingSnooze}
          >
            {performingSnooze ? <CircularProgress size={18} /> : "Snooze"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications - positioned at top center */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <MuiAlert
          onClose={() => setSnackbarOpen(false)}
          severity="info"
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </Box>
  );
};

export default AlertsTab;
